import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { adminAPI, clubAPI } from '../services/api';
import './AdminPage.css';

const PlayersTab = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasSearched, setHasSearched] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) {
      setError('Please enter a search term');
      return;
    }

    setLoading(true);
    setError('');
    setHasSearched(true);

    try {
      const response = await adminAPI.getAllUsers();
      const allUsers = response.data || [];
      
      // Filter users by name or email (case-insensitive)
      const query = searchQuery.toLowerCase().trim();
      const filtered = allUsers.filter(user => {
        const name = (user.name || '').toLowerCase();
        const email = (user.email || '').toLowerCase();
        return name.includes(query) || email.includes(query);
      });

      setSearchResults(filtered);
    } catch (err) {
      console.error('Search error:', err);
      setError('Failed to search users');
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {error && <div className="error-message">{error}</div>}

      <div className="card">
        <form onSubmit={handleSearch} className="admin-search-form">
          <div className="form-group">
            <label className="form-label">Search Users</label>
            <p className="form-helper">Search by name or email</p>
            <div className="search-input-group">
              <input
                type="text"
                className="form-input"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Enter name or email..."
                disabled={loading}
              />
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading || !searchQuery.trim()}
              >
                {loading ? 'Searching...' : 'Search'}
              </button>
            </div>
          </div>
        </form>
      </div>

      {hasSearched && (
        <div className="card" style={{ marginTop: '24px' }}>
          <h2>Search Results</h2>
          {searchResults.length === 0 ? (
            <div className="empty-state">
              <p>No users found matching your search.</p>
            </div>
          ) : (
            <div className="admin-users-list">
              {searchResults.map((user) => (
                <div key={user.userId} className="admin-user-item">
                  <div className="admin-user-info">
                    <h3>{user.name || 'Unknown'}</h3>
                    <p className="admin-user-email">{user.email || 'No email'}</p>
                  </div>
                  <div className="admin-user-actions">
                    <Link
                      to={`/players/${user.userId}`}
                      className="btn btn-secondary"
                      style={{ marginRight: '8px' }}
                    >
                      View Profile
                    </Link>
                    <Link
                      to={`/settings?userId=${user.userId}`}
                      className="btn btn-primary"
                    >
                      View Settings
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
};

const ClubsTab = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasSearched, setHasSearched] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) {
      setError('Please enter a search term');
      return;
    }

    setLoading(true);
    setError('');
    setHasSearched(true);

    try {
      const response = await clubAPI.getAllClubs();
      const allClubs = response.data || [];
      
      // Filter clubs by name (case-insensitive)
      const query = searchQuery.toLowerCase().trim();
      const filtered = allClubs.filter(club => {
        const name = (club.name || '').toLowerCase();
        return name.includes(query);
      });

      setSearchResults(filtered);
    } catch (err) {
      console.error('Search error:', err);
      setError('Failed to search clubs');
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {error && <div className="error-message">{error}</div>}

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '20px' }}>
          <Link to="/clubs/create" className="btn btn-primary">
            Create New Club
          </Link>
        </div>

        <form onSubmit={handleSearch} className="admin-search-form">
          <div className="form-group">
            <label className="form-label">Search Clubs</label>
            <p className="form-helper">Search by name</p>
            <div className="search-input-group">
              <input
                type="text"
                className="form-input"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Enter club name..."
                disabled={loading}
              />
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading || !searchQuery.trim()}
              >
                {loading ? 'Searching...' : 'Search'}
              </button>
            </div>
          </div>
        </form>
      </div>

      {hasSearched && (
        <div className="card" style={{ marginTop: '24px' }}>
          <h2>Search Results</h2>
          {searchResults.length === 0 ? (
            <div className="empty-state">
              <p>No clubs found matching your search.</p>
            </div>
          ) : (
            <div className="admin-users-list">
              {searchResults.map((club) => (
                <div key={club.clubId} className="admin-user-item">
                  <div className="admin-user-info">
                    <h3>{club.name || 'Unknown'}</h3>
                    <p className="admin-user-email">
                      {club.members?.length || 0} member{club.members?.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                  <div className="admin-user-actions">
                    <Link
                      to={`/clubs/${club.clubId}`}
                      className="btn btn-primary"
                    >
                      View Club
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
};

const AdminPage = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('players');

  if (!isAdmin) {
    navigate('/leagues');
    return null;
  }

  return (
    <div className="page-container">
      <div className="container">
        <div className="page-header">
          <h1 className="page-title">Admin Panel</h1>
          <p className="page-subtitle">
            {activeTab === 'players' ? 'Search and manage users' : 'Search and manage clubs'}
          </p>
        </div>

        <div className="admin-tabs">
          <button
            className={`admin-tab ${activeTab === 'players' ? 'active' : ''}`}
            onClick={() => setActiveTab('players')}
          >
            Players
          </button>
          <button
            className={`admin-tab ${activeTab === 'clubs' ? 'active' : ''}`}
            onClick={() => setActiveTab('clubs')}
          >
            Clubs
          </button>
        </div>

        {activeTab === 'players' ? <PlayersTab /> : <ClubsTab />}
      </div>
    </div>
  );
};

export default AdminPage;
